import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Globe, BookOpen, Award, Users, GraduationCap } from 'lucide-react';

const Programs = () => {
  const programs = [
    {
      level: "Master's Degree",
      title: 'Master of Humanitarian Action & Development',
      duration: '1 Year',
      courses: '9 Courses + Capstone Field Project',
      fullFee: '$2,000',
      scholarshipFee: '$200',
      savings: '$1,800',
      link: '/program/masters',
      requirements: [
        "Bachelor's degree or equivalent",
        'Proficiency in English',
        'Commitment to humanitarian work',
        'Access to reliable internet'
      ],
      description: 'A comprehensive program designed for professionals entering or advancing in the humanitarian sector. Covers all essential aspects of humanitarian action from foundations to leadership.',
      ideal: 'Early to mid-career professionals, NGO workers, career changers entering humanitarian field'
    },
    {
      level: "Master's Degree",
      title: 'Master of Development Project & Program Management',
      duration: '9 Months',
      courses: '5 Courses + Capstone Field Project',
      fullFee: '$3,500',
      scholarshipFee: '$350',
      savings: '$3,150',
      link: '/program/masters-ppm',
      requirements: [
        "Bachelor's degree or equivalent",
        'Proficiency in English',
        'Interest in development management',
        'Access to reliable internet'
      ],
      description: 'A focused program for professionals seeking specialized skills in development project and program management, MEAL frameworks, development finance, and Theory of Change.',
      ideal: 'Development professionals, project managers, M&E specialists, program coordinators'
    },
    {
      level: 'Doctoral Degree',
      title: 'Doctor of Humanitarian Action & Development',
      duration: '3 Years',
      courses: '8 Courses + Doctoral Dissertation',
      fullFee: '$10,800',
      scholarshipFee: '$1,080',
      savings: '$9,720',
      link: '/program/doctoral',
      requirements: [
        "Master's degree in relevant field",
        '3-5 years professional experience',
        'Proficiency in English',
        'Research aptitude and commitment'
      ],
      description: 'An advanced research-focused program for experienced professionals seeking to lead innovation in humanitarian practice, policy, and scholarship.',
      ideal: 'Senior professionals, researchers, policy makers, aspiring academics and thought leaders'
    }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="programs-page">
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-[#F1F5F8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="text-sm font-medium uppercase tracking-widest text-[#163A59] mb-4">
              Academic Programs
            </p>
            <h1 className="font-['Source_Serif_4'] text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
              Our Programs
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
              Choose from our Master's or Doctoral programs in Humanitarian Action & Development. 
              All programs are delivered 100% online and offer 90% scholarships for eligible 
              Afro-Asia students.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Programs Comparison */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-3 gap-8">
            {programs.map((program, index) => (
              <motion.div
                key={program.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white border border-slate-200 hover:border-[#163A59]/30 hover:shadow-xl transition-all duration-300"
              >
                {/* Header */}
                <div className={`p-6 ${index === 1 ? 'bg-[#163A59] border-l-4 border-[#C82032]' : 'bg-[#163A59]'} text-white`}>
                  <p className="text-sm font-medium uppercase tracking-wider opacity-90 mb-1">
                    {program.level}
                  </p>
                  <h2 className="font-['Source_Serif_4'] text-xl font-bold">
                    {program.title}
                  </h2>
                </div>

                {/* Content */}
                <div className="p-8">
                  <p className="text-slate-600 mb-6">{program.description}</p>

                  {/* Key Info */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-[#163A59]" />
                      <div>
                        <p className="text-xs text-slate-500">Duration</p>
                        <p className="font-semibold text-slate-900">{program.duration}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <BookOpen className="w-5 h-5 text-[#163A59]" />
                      <div>
                        <p className="text-xs text-slate-500">Curriculum</p>
                        <p className="font-semibold text-slate-900">{program.courses}</p>
                      </div>
                    </div>
                  </div>

                  {/* Pricing */}
                  <div className="bg-[#F1F5F8] p-6 mb-6">
                    <div className="mb-4">
                      <p className="text-sm text-slate-500">Full Tuition</p>
                      <p className="text-2xl font-bold text-slate-900">{program.fullFee}</p>
                    </div>
                    <div className="flex items-start gap-2 text-slate-700 pt-3 border-t border-[#DCE4EA]">
                      <Award size={16} className="text-[#C82032] mt-0.5 flex-shrink-0" />
                      <span className="text-sm">
                        <span className="font-semibold text-[#C82032]">90% Scholarship</span> available for eligible Afro-Asia students
                      </span>
                    </div>
                  </div>

                  {/* Requirements */}
                  <div className="mb-6">
                    <h4 className="font-semibold text-slate-900 mb-3">Entry Requirements</h4>
                    <ul className="space-y-2">
                      {program.requirements.map((req, i) => (
                        <li key={`req-${req.slice(0, 15)}`} className="flex items-start gap-2 text-sm text-slate-600">
                          <span className="w-1.5 h-1.5 bg-[#163A59] rounded-full mt-2" />
                          {req}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Ideal For */}
                  <div className="mb-8 p-4 bg-[#163A59]/5 border-l-4 border-[#163A59]">
                    <p className="text-sm text-slate-600 mb-1">Ideal For</p>
                    <p className="text-slate-700">{program.ideal}</p>
                  </div>

                  {/* CTA */}
                  <div className="flex gap-4">
                    <Link
                      to={program.link}
                      className="flex-1 inline-flex items-center justify-center gap-2 bg-[#163A59] hover:bg-[#102A43] text-white px-6 py-3 rounded-sm font-medium transition-all"
                    >
                      Learn More
                    </Link>
                    <Link
                      to="/admissions"
                      className="flex-1 inline-flex items-center justify-center gap-2 bg-[#C82032] hover:bg-[#A81828] text-white px-6 py-3 rounded-sm font-medium transition-all"
                    >
                      Apply Now
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 md:py-32 bg-[#F1F5F8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              Program Comparison
            </h2>
            <p className="text-slate-600">Side-by-side comparison of our two programs</p>
          </div>

          <div className="overflow-x-auto -mx-6 md:mx-0 px-6 md:px-0">
            <p className="text-xs text-slate-500 mb-3 lg:hidden">← Swipe to see all columns →</p>
            <table className="w-full min-w-[640px] bg-white border border-slate-200">
              <thead>
                <tr className="bg-[#163A59] text-white">
                  <th className="p-4 text-left font-semibold">Feature</th>
                  <th className="p-4 text-center font-semibold">Master's (Humanitarian)</th>
                  <th className="p-4 text-center font-semibold border-l-2 border-[#C82032]">Master's (PPM)</th>
                  <th className="p-4 text-center font-semibold bg-[#163A59]">Doctoral</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-slate-200">
                  <td className="p-4 font-medium text-slate-900">Duration</td>
                  <td className="p-4 text-center text-slate-600">1 Year</td>
                  <td className="p-4 text-center text-slate-600">9 Months</td>
                  <td className="p-4 text-center text-slate-600">3 Years</td>
                </tr>
                <tr className="border-b border-slate-200 bg-[#F1F5F8]">
                  <td className="p-4 font-medium text-slate-900">Full Tuition</td>
                  <td className="p-4 text-center text-slate-600">$2,000</td>
                  <td className="p-4 text-center text-slate-600">$3,500</td>
                  <td className="p-4 text-center text-slate-600">$10,800</td>
                </tr>
                <tr className="border-b border-slate-200">
                  <td className="p-4 font-medium text-slate-900">Scholarship</td>
                  <td className="p-4 text-center text-[#C82032] font-semibold" colSpan="3">90% scholarship available for eligible Afro-Asia students (all programs)</td>
                </tr>
                <tr className="border-b border-slate-200 bg-[#F1F5F8]">
                  <td className="p-4 font-medium text-slate-900">Courses</td>
                  <td className="p-4 text-center text-slate-600">9 Courses</td>
                  <td className="p-4 text-center text-slate-600">5 Courses</td>
                  <td className="p-4 text-center text-slate-600">8 Courses</td>
                </tr>
                <tr className="border-b border-slate-200">
                  <td className="p-4 font-medium text-slate-900">Final Project</td>
                  <td className="p-4 text-center text-slate-600">Capstone (3 months)</td>
                  <td className="p-4 text-center text-slate-600">Capstone (4 months)</td>
                  <td className="p-4 text-center text-slate-600">Dissertation (12 months)</td>
                </tr>
                <tr className="border-b border-slate-200 bg-[#F1F5F8]">
                  <td className="p-4 font-medium text-slate-900">Entry Requirement</td>
                  <td className="p-4 text-center text-slate-600">Bachelor's Degree</td>
                  <td className="p-4 text-center text-slate-600">Bachelor's Degree</td>
                  <td className="p-4 text-center text-slate-600">Master's + Experience</td>
                </tr>
                <tr className="border-b border-slate-200">
                  <td className="p-4 font-medium text-slate-900">Delivery Mode</td>
                  <td className="p-4 text-center text-slate-600">100% Online</td>
                  <td className="p-4 text-center text-slate-600">100% Online</td>
                  <td className="p-4 text-center text-slate-600">100% Online</td>
                </tr>
                <tr>
                  <td className="p-4 font-medium text-slate-900">90% Scholarship</td>
                  <td className="p-4 text-center text-green-700">✓ Available</td>
                  <td className="p-4 text-center text-green-700">✓ Available</td>
                  <td className="p-4 text-center text-green-700">✓ Available</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32 bg-[#163A59]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Advance Your Career?
            </h2>
            <p className="text-lg text-white/80 max-w-2xl mx-auto mb-10">
              Take the next step in your humanitarian journey. Apply now and 
              join our global community of change-makers.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/admissions"
                className="inline-flex items-center justify-center gap-2 bg-[#C82032] hover:bg-[#A81828] text-white px-10 py-4 rounded-sm font-medium transition-all"
              >
                Start Your Application
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white border border-white/30 px-10 py-4 rounded-sm font-medium transition-all"
              >
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Programs;
